import React, { useState, useEffect, useRef } from "react";
import queryString from "query-string";
import { useLocation, useParams } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Card from "../../components/Card";
import { SKU_MAP } from "./SkuMap";

const RedirectToStripeCheckout = (props) => {
  const location = useLocation();
  const { success } = useParams();
  const [error, setError] = useState(null);
  const hasRun = useRef(false);

  const STRIPE_PUBLIC_KEY = window.appConfig.stripe_public_key;

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const queryParams = queryString.parse(location.search);
    let scriptElement = null;

    const loadStripe = (callback) => {
      if (window.Stripe) {
        callback();
        return;
      }
      scriptElement = document.createElement("script");
      scriptElement.type = "text/javascript";
      scriptElement.src = "https://js.stripe.com/v3/";
      document.head.appendChild(scriptElement);
      scriptElement.onload = callback;
    };

    const handleStripeResponse = (res) => {
      if (res.success) {
        const stripe = window.Stripe(STRIPE_PUBLIC_KEY);
        stripe
          .redirectToCheckout({
            sessionId: res.session_id,
          })
          .then(function (result) {
            if (result.error) {
              setError(result.error.message);
            }
          });
      } else {
        setError(res.error);
      }
    };

    if (typeof queryParams.req != "undefined") {
      const requestId = queryParams.req;
      loadStripe(() => {
        fetch("/api/clientPaymentRequest/" + requestId)
          .then((res) => res.json())
          .then(handleStripeResponse)
          .catch((err) => {
            setError(err.message || "Failed to load payment details");
          });
      });
    } else if (typeof queryParams.ref == "undefined") {
      setError("Incorrect payment url");
    } else {
      loadStripe(() => {
        const stripe = window.Stripe(STRIPE_PUBLIC_KEY);
        var paymentId;
        var paymentSku;

        if ("product" in queryParams) {
          paymentSku = queryParams.product;
        } else {
          // Default to £250 up-front fee if no payId is set
          if (typeof queryParams.payId == "undefined") {
            paymentSku = "sku_FwsX1BlDTfkAnZ"; // Note: this sku is for £250 up-front fee
          } else {
            paymentId = queryParams.payId;
            paymentSku = SKU_MAP.get(paymentId);
          }
        }

        stripe
          .redirectToCheckout({
            items: [{ sku: paymentSku, quantity: 1 }],
            successUrl: "https://client.juno.legal/payment/true",
            cancelUrl: "https://client.juno.legal/payment/false",
            billingAddressCollection: "required",
            clientReferenceId: queryParams.ref,
          })
          .then(function (result) {
            if (result.error) {
              setError(result.error.message);
            }
          });
      });
    }

    return () => {
      if (scriptElement?.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, [location.search, STRIPE_PUBLIC_KEY]);

  if (typeof success == "undefined") {
    if (error) {
      return (
        <PageLayout>
          <Card title="That didn’t work" removeTopPadding>
            <p>There has been an error: {error}</p>
          </Card>
        </PageLayout>
      );
    } else {
      return (
        <PageLayout>
          <Card title="Redirecting" removeTopPadding>
            <p>Redirecting to payment screen...</p>
          </Card>
        </PageLayout>
      );
    }
  } else {
    if (success === "true") {
      return (
        <PageLayout>
          <ScrollToTop />
          <Card title="Thank you!" removeTopPadding>
            <p>You’ll get a receipt for your payment from Stripe in the next few minutes.</p>
          </Card>

          <Card title="What happens next" removeTopPadding>
            <p>We’ll add your payment to your case automatically, so you don’t need to send us proof you’ve paid.</p>
          </Card>

          <Card title="Sending us money as your case progresses" removeTopPadding>
            <p>
              Our bank details will be on your Juno dashboard. Use these details when you need to send us money in
              future.
            </p>
            <p>
              Our bank details <strong>will not change</strong> for the whole of your transaction. We’ll never call or
              email you to say they have.
            </p>
            <p>
              If someone tells you our bank details have changed, they’re a fraudster. Do not send them any money. Call
              us straight away on <a href="tel:+442038563339">020 3856 3339</a>.
            </p>
          </Card>
        </PageLayout>
      );
    } else {
      return (
        <PageLayout>
          <Card title="Payment cancelled" removeTopPadding>
            <p>To try again, click the link in the email.</p>
          </Card>
        </PageLayout>
      );
    }
  }
};

export default RedirectToStripeCheckout;
